/**
 * Splyt tracking plan, web mirror.
 *
 * Source of truth: analytics/events.json at the workspace root (the human
 * version is tracking-plan.md next to it). Every name here must match that
 * file byte for byte; events.test.js pins the list against it. To add or
 * change an event: change the plan first, then this file, then the call sites.
 *
 * Conventions: event names are object_action, lowercase snake_case, past
 * tense. Properties are snake_case, ids end in _id, booleans start with is_ or
 * has_, money is in _cents, enum values are lowercase snake_case. Ids and enums
 * only for people: never a person's name, phone number, or handle on an event
 * (those live on the Mixpanel profile). Places may be named on their own
 * events (restaurant_name). Never item text, free text, or whole objects.
 *
 * Identity: distinct_id is the Firebase uid. identify() only after a real
 * (non anonymous) sign in, never for anonymous Firebase users. reset() on
 * sign out. `platform` is a super property registered once at start.
 */

/** Every event the web app may fire. */
export const EVENTS = Object.freeze({
  ACCOUNT_CREATED: 'account_created',
  SIGNED_IN: 'signed_in',
  RECEIPT_SCANNED: 'receipt_scanned',
  RECEIPT_SAVED: 'receipt_saved',
  INVITE_SHARED: 'invite_shared',
  RECEIPT_OPENED: 'receipt_opened',
  ITEMS_CLAIMED: 'items_claimed',
  PAYMENT_STARTED: 'payment_started',
  PAYMENT_CONFIRMED: 'payment_confirmed',
  APP_DOWNLOAD_CLICKED: 'app_download_clicked',
  GROUP_INVITE_OPENED: 'group_invite_opened',
  SCREEN_VIEWED: 'screen_viewed',
  PROMPT_SHOWN: 'prompt_shown',
  PROMPT_RESPONDED: 'prompt_responded',
  ONBOARDING_STEP_COMPLETED: 'onboarding_step_completed',
  RECEIPT_EDITED: 'receipt_edited',
  PROFILE_UPDATED: 'profile_updated',
  ERROR_OCCURRED: 'error_occurred',
});

/** The same names as a sorted list, for the name lock test and the release audit. */
export const EVENT_NAMES = Object.freeze(Object.values(EVENTS).slice().sort());

// ─── Shared enums (values are the plan's, byte for byte) ──────────────────────

/**
 * Which product surface the moment belongs to: a standalone receipt split, a
 * group, or organic (account and sign in events with no link involved).
 * Plan v1.1 renamed receipt from dining; the value dining no longer exists.
 * Plan v1.2 added referral (the free splits invite); it appears only on
 * invite_shared and is mirrored here because the name lock compares the
 * whole vocabulary.
 */
export const SURFACE = Object.freeze({
  RECEIPT: 'receipt',
  GROUP: 'group',
  ORGANIC: 'organic',
  REFERRAL: 'referral',
});

/** Normalized link sources. See helpers.js normalizeLinkSource for the utm_source rule. */
export const LINK_SOURCE = Object.freeze({
  SHARE_SHEET: 'share_sheet',
  SMS_BLAST: 'sms_blast',
  QR: 'qr',
  COPY_LINK: 'copy_link',
  RECEIPT_TEXT: 'receipt_text',
  GROUP_INVITE_TEXT: 'group_invite_text',
  NUDGE: 'nudge',
  DIRECT: 'direct',
});

export const VIEWER_ROLE = Object.freeze({
  HOST: 'host',
  GUEST: 'guest',
});

export const CLAIMED_FOR = Object.freeze({
  SELF: 'self',
  OTHER: 'other',
});

/**
 * unknown is valid on payment_confirmed only. The plan's values are snake_case
 * (cash_app, cash_other) while the payment record's own keys are camelCase
 * (cashApp, cashOther): map between them deliberately, never by accident.
 */
export const PAYMENT_METHOD = Object.freeze({
  VENMO: 'venmo',
  CASH_APP: 'cash_app',
  CARD: 'card',
  ZELLE: 'zelle',
  CASH_OTHER: 'cash_other',
  UNKNOWN: 'unknown',
});

export const CONFIRMED_BY = Object.freeze({
  PAYER: 'payer',
  HOST: 'host',
});

export const STORE = Object.freeze({
  IOS: 'ios',
  ANDROID: 'android',
});

/** Which get-the-app control was tapped (app_download_clicked.source). */
export const DOWNLOAD_SOURCE = Object.freeze({
  PARTICIPANT_SUMMARY: 'participant_summary',
  PARTICIPANT_SUMMARY_TOP_BANNER: 'participant_summary_top_banner',
  PARTICIPANT_SUMMARY_COUNTDOWN_BANNER: 'participant_summary_countdown_banner',
  RECEIPT_TEXT_BANNER: 'receipt_text_banner',
  RECEIPT_TEXT_PAGE: 'receipt_text_page',
  SUMMARY_PAID_JOIN: 'summary_paid_join',
  GROUP_HANDOFF: 'group_handoff',
  GROUP_LANDING: 'group_landing',
});

/** invite_shared.channel. Web fires share_sheet and qr; the rest are mobile. */
export const SHARE_CHANNEL = Object.freeze({
  SHARE_SHEET: 'share_sheet',
  MESSAGE: 'message',
  COPY_LINK: 'copy_link',
  QR: 'qr',
  SMS_BLAST: 'sms_blast',
  CONTACTS_SMS: 'contacts_sms',
});

/** invite_shared.source, the control that was used. Web fires share_modal. */
export const SHARE_SOURCE = Object.freeze({
  HEADER: 'header',
  FOOTER: 'footer',
  INVITE_SHEET: 'invite_sheet',
  INSTRUCTIONS_MODAL: 'instructions_modal',
  SHARE_MODAL: 'share_modal',
  DASHBOARD: 'dashboard',
  ADD_MEMBERS: 'add_members',
  SETTINGS: 'settings',
  SETUP: 'setup',
  REFERRAL_SCREEN: 'referral_screen',
});

export const SCAN_METHOD = Object.freeze({
  CAMERA: 'camera',
  LIBRARY: 'library',
  DIGITAL_RECEIPT: 'digital_receipt',
});

export const INVITE_SOURCE = Object.freeze({
  WEB_LINK: 'web_link',
  IN_APP_BANNER: 'in_app_banner',
});

/** screen_viewed.screen_name. Manual, only these screens. */
export const SCREEN = Object.freeze({
  HOME: 'home',
  RECEIPT_INVITE: 'receipt_invite',
  PARTICIPANT_BREAKDOWN: 'participant_breakdown',
  PHONE_AUTH: 'phone_auth',
  VERIFY: 'verify',
  EDIT_RECEIPT: 'edit_receipt',
  GUEST_LIST: 'guest_list',
  MY_RECEIPTS: 'my_receipts',
  REFERRAL: 'referral',
  ONBOARDING_RATING: 'onboarding_rating',
  ONBOARDING_INSTRUCTIONS: 'onboarding_instructions',
  REQUEST_PERMISSIONS: 'request_permissions',
  EDIT_PROFILE: 'edit_profile',
  FEEDBACK: 'feedback',
  DIGITAL_RECEIPT: 'digital_receipt',
  GROUP_ADD_PEOPLE: 'group_add_people',
  UPSELL_EDIT_RECEIPT: 'upsell_edit_receipt',
  UPSELL_SELECT_AS: 'upsell_select_as',
  NAME_EMOJI: 'name_emoji',
});

/**
 * prompt_shown.prompt and prompt_responded.prompt. RECEIPT_TEXT_PAGE is a
 * prompt_responded only value: the texted receipt's read-only page answered
 * with "Edit my selections".
 */
export const PROMPT = Object.freeze({
  GROUPS_INTRO: 'groups_intro',
  UGC_CAMPAIGN: 'ugc_campaign',
  STORE_REVIEW: 'store_review',
  IS_THIS_YOU: 'is_this_you',
  RECEIPT_HOLD: 'receipt_hold',
  CONTACTS_SOFT_ASK: 'contacts_soft_ask',
  CONTACTS_PERMISSION: 'contacts_permission',
  GROUP_ADDED_BANNER: 'group_added_banner',
  UPLOAD_INSTRUCTIONS: 'upload_instructions',
  DID_YOU_PAY: 'did_you_pay',
  PAYWALL_EXIT_SURVEY: 'paywall_exit_survey',
  PAYWALL: 'paywall',
  NOTIFICATIONS_PERMISSION: 'notifications_permission',
  RECEIPT_TEXT_PAGE: 'receipt_text_page',
  RECAPTCHA_CHALLENGE: 'recaptcha_challenge',
  CURRENCY_CONVERT: 'currency_convert',
});

export const RESPONSE = Object.freeze({
  CTA: 'cta',
  DISMISS: 'dismiss',
  ALLOW: 'allow',
  DENY: 'deny',
  GRANTED: 'granted',
  DENIED: 'denied',
  NOT_ME: 'not_me',
  JOINED_NEW: 'joined_new',
  YES: 'yes',
  NO: 'no',
  EDIT_SELECTIONS: 'edit_selections',
  REFER_FRIENDS: 'refer_friends',
  REASON: 'reason',
  OVERRIDE: 'override',
});

export const ONBOARDING_STEP = Object.freeze({
  RATING: 'rating',
  PERMISSIONS: 'permissions',
  SURVEY: 'survey',
  NAME: 'name',
});

export const EDIT_TYPE = Object.freeze({
  ITEM_EDITED: 'item_edited',
  ITEM_ADDED: 'item_added',
  ITEM_DELETED: 'item_deleted',
  TIP: 'tip',
  SPLIT_COUNT: 'split_count',
});

export const EDIT_SCREEN = Object.freeze({
  VERIFY: 'verify',
  EDIT: 'edit',
});

export const PROFILE_FIELD = Object.freeze({
  NAME: 'name',
  PHOTO: 'photo',
  PAYMENT_INFO: 'payment_info',
});

// ─── Types (JSDoc; TypeScript files importing this module get them for free) ──

/** @typedef {typeof EVENTS[keyof typeof EVENTS]} EventName */
/** @typedef {typeof SURFACE[keyof typeof SURFACE]} Surface */
/** @typedef {typeof LINK_SOURCE[keyof typeof LINK_SOURCE]} LinkSource */
/** @typedef {typeof VIEWER_ROLE[keyof typeof VIEWER_ROLE]} ViewerRole */
/** @typedef {typeof CLAIMED_FOR[keyof typeof CLAIMED_FOR]} ClaimedFor */
/** @typedef {typeof PAYMENT_METHOD[keyof typeof PAYMENT_METHOD]} PaymentMethod */
/** @typedef {typeof CONFIRMED_BY[keyof typeof CONFIRMED_BY]} ConfirmedBy */
/** @typedef {typeof STORE[keyof typeof STORE]} Store */
/** @typedef {typeof DOWNLOAD_SOURCE[keyof typeof DOWNLOAD_SOURCE]} DownloadSource */
/** @typedef {typeof SHARE_CHANNEL[keyof typeof SHARE_CHANNEL]} ShareChannel */
/** @typedef {typeof SHARE_SOURCE[keyof typeof SHARE_SOURCE]} ShareSource */
/** @typedef {typeof SCAN_METHOD[keyof typeof SCAN_METHOD]} ScanMethod */
/** @typedef {typeof INVITE_SOURCE[keyof typeof INVITE_SOURCE]} InviteSource */
/** @typedef {typeof SCREEN[keyof typeof SCREEN]} ScreenName */
/** @typedef {typeof PROMPT[keyof typeof PROMPT]} Prompt */
/** @typedef {typeof RESPONSE[keyof typeof RESPONSE]} Response */
/** @typedef {typeof ONBOARDING_STEP[keyof typeof ONBOARDING_STEP]} OnboardingStep */
/** @typedef {typeof EDIT_TYPE[keyof typeof EDIT_TYPE]} EditType */
/** @typedef {typeof EDIT_SCREEN[keyof typeof EDIT_SCREEN]} EditScreen */
/** @typedef {typeof PROFILE_FIELD[keyof typeof PROFILE_FIELD]} ProfileField */

/**
 * A brand new Splyt account (first ever phone verification for that number).
 * Also people.set_once the signup_* profile properties: see track.js trackAccountCreated.
 * @typedef {Object} AccountCreatedProps
 * @property {Surface} surface
 * @property {string} [invite_id]
 * @property {string} [referrer_user_id] The host (receipt) or inviter (group) whose link brought this person in.
 * @property {string} [receipt_id] receipt guest signups
 * @property {string} [group_id] group invite signups
 */

/**
 * Someone finished phone verification, new or returning. Never the phone number.
 * @typedef {Object} SignedInProps
 * @property {Surface} surface
 * @property {boolean} is_new_user
 * @property {string} [receipt_id] receipt wall: the receipt being claimed
 * @property {string} [group_id] group join
 * @property {string} [invite_id]
 */

/**
 * A receipt was read successfully by OCR. No receipt payload.
 * @typedef {Object} ReceiptScannedProps
 * @property {'receipt' | 'group'} surface
 * @property {ScanMethod} method
 * @property {string} [receipt_id]
 * @property {string} [restaurant_name] the merchant name on the receipt, when known (a place, never a person)
 */

/**
 * The host reviewed the scanned receipt and saved it.
 * @typedef {Object} ReceiptSavedProps
 * @property {string} receipt_id
 * @property {'receipt' | 'group'} surface
 * @property {number} [item_count]
 * @property {string} [restaurant_name] the merchant name on the receipt, when known (a place, never a person)
 */

/**
 * Someone invited people to something: a receipt link, a group, or Splyt
 * itself (referral). One event for every surface so invites per user is one
 * number. Web fires surface receipt only: the share modal's share button
 * (share_sheet) and the QR code being shown (qr).
 * @typedef {Object} InviteSharedProps
 * @property {'receipt' | 'group' | 'referral'} surface
 * @property {ShareChannel} channel
 * @property {ShareSource} [source]
 * @property {string} [receipt_id]
 * @property {string} [group_id]
 * @property {number} [recipient_count] sms_blast and contacts_sms only
 */

/**
 * Someone opened a receipt page. Hosts viewing their own receipt are marked by viewer_role.
 * @typedef {Object} ReceiptOpenedProps
 * @property {string} receipt_id
 * @property {string} host_user_id
 * @property {ViewerRole} viewer_role
 * @property {LinkSource | string} source normalized from utm_source
 * @property {boolean} [is_signed_in]
 * @property {string} [restaurant_name] the merchant name on the receipt, when known (a place, never a person)
 */

/**
 * A guest finished picking their items (Claim in the loop vocabulary).
 * @typedef {Object} ItemsClaimedProps
 * @property {string} receipt_id
 * @property {'receipt' | 'group'} surface
 * @property {number} [item_count]
 * @property {ClaimedFor} claimed_for
 * @property {boolean} [receipt_fully_claimed] group receipts only
 * @property {string} [expense_id] group receipts only
 */

/**
 * A guest committed to a way to pay and left the flow to pay: handed to Venmo
 * or Cash App, sent to the host's Zelle details, or paying in cash. Exactly one
 * per payment, always on the guest's own tap and never from a render.
 * Only venmo and cash_app open another app; zelle fires when the guest reaches
 * the Zelle step and cash_other when they pick the cash option. Copying the
 * Zelle details is a convenience, not the commitment, so it fires nothing.
 * @typedef {Object} PaymentStartedProps
 * @property {string} receipt_id
 * @property {'venmo' | 'cash_app' | 'card' | 'zelle' | 'cash_other'} method
 * @property {number} [amount_cents]
 */

/**
 * A payment was confirmed by the guest (Did you send it? yes) or by the host.
 * @typedef {Object} PaymentConfirmedProps
 * @property {string} receipt_id
 * @property {PaymentMethod} method
 * @property {ConfirmedBy} confirmed_by
 * @property {number} [amount_cents]
 * @property {string} [participant_user_id] when the host confirms for someone
 */

/**
 * A web visitor tapped a get-the-app control. True installs come from App Store Connect.
 * @typedef {Object} AppDownloadClickedProps
 * @property {Store} store
 * @property {DownloadSource} source
 * @property {'receipt' | 'group'} surface
 * @property {string} [receipt_id]
 * @property {string} [group_id]
 * @property {string} [invite_id]
 */

/**
 * An invited person opened the group invite page.
 * @typedef {Object} GroupInviteOpenedProps
 * @property {string} group_id
 * @property {string} [inviter_user_id] from ?inviter=
 * @property {string} [invite_id] from ?i= (the short link code)
 * @property {InviteSource} source
 */

/**
 * A screen or page was shown. Manual, only the screens in SCREEN.
 * @typedef {Object} ScreenViewedProps
 * @property {ScreenName} screen_name
 * @property {string} [receipt_id]
 * @property {string} [group_id]
 */

/**
 * A modal, banner, or system prompt appeared.
 * @typedef {Object} PromptShownProps
 * @property {Prompt} prompt
 * @property {string} [source]
 * @property {string} [receipt_id] when the prompt is about a receipt (did you pay, receipt hold)
 * @property {string} [group_id] when the prompt is about a group (the you were added banner, is this you)
 */

/**
 * Someone answered a prompt.
 * @typedef {Object} PromptRespondedProps
 * @property {Prompt | string} prompt
 * @property {Response} response
 * @property {string} [reason] preset reason only, never free text
 * @property {string} [source]
 * @property {string} [receipt_id] when the prompt is about a receipt (did you pay, receipt hold)
 * @property {string} [group_id] when the prompt is about a group (the you were added banner, is this you)
 */

/**
 * A new member finished an onboarding step. Never the value they entered.
 * @typedef {Object} OnboardingStepCompletedProps
 * @property {OnboardingStep} step
 * @property {Surface} [surface]
 */

/**
 * The host changed something on a receipt. No item objects, no before/after.
 * @typedef {Object} ReceiptEditedProps
 * @property {string} [receipt_id]
 * @property {EditType} edit_type
 * @property {EditScreen} screen
 */

/**
 * Someone changed their profile. Never the handles on the event: payment_info
 * also setPeople({ venmo_handle, cash_app_handle }) so the profile carries them.
 * The Zelle value is the exception: it is a phone number or an email address,
 * so it never reaches Mixpanel at all, on an event or on a profile. has_zelle
 * is the only thing Mixpanel learns about it.
 * @typedef {Object} ProfileUpdatedProps
 * @property {ProfileField} field
 * @property {string} [source]
 * @property {boolean} [has_venmo]
 * @property {boolean} [has_cash_app]
 * @property {boolean} [has_zelle]
 * @property {boolean} [has_cash_other]
 */

/**
 * Something failed. No stack traces, no payload objects.
 * @typedef {Object} ErrorOccurredProps
 * @property {string} [screen]
 * @property {string} function
 * @property {string} [code]
 * @property {string} [message]
 * @property {boolean} is_recovered
 * @property {string} [feature]
 */

/**
 * Properties by event name; track() types its second argument with this.
 * @typedef {Object} EventProps
 * @property {AccountCreatedProps} account_created
 * @property {SignedInProps} signed_in
 * @property {ReceiptScannedProps} receipt_scanned
 * @property {ReceiptSavedProps} receipt_saved
 * @property {InviteSharedProps} invite_shared
 * @property {ReceiptOpenedProps} receipt_opened
 * @property {ItemsClaimedProps} items_claimed
 * @property {PaymentStartedProps} payment_started
 * @property {PaymentConfirmedProps} payment_confirmed
 * @property {AppDownloadClickedProps} app_download_clicked
 * @property {GroupInviteOpenedProps} group_invite_opened
 * @property {ScreenViewedProps} screen_viewed
 * @property {PromptShownProps} prompt_shown
 * @property {PromptRespondedProps} prompt_responded
 * @property {OnboardingStepCompletedProps} onboarding_step_completed
 * @property {ReceiptEditedProps} receipt_edited
 * @property {ProfileUpdatedProps} profile_updated
 * @property {ErrorOccurredProps} error_occurred
 */

/**
 * Set once on the user profile at account_created. Never overwritten, so
 * acquisition source is a permanent cohort dimension.
 * @typedef {Object} SignupProfile
 * @property {'web'} signup_platform
 * @property {Surface} signup_surface
 * @property {string} [signup_referrer_user_id]
 * @property {string} [signup_receipt_id]
 * @property {string} [signup_group_id]
 * @property {string} [signup_invite_id]
 * @property {LinkSource | string} signup_source
 * @property {string} signup_at ISO datetime
 */

export {};
